import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from 'recharts';
import { useThemeStore } from '../../store/ConfigStore';

interface DataPoint {
  time: string;
  value: number;
}

interface ChartProps {
  data?: DataPoint[];
  height?: number | string;
  displayedDecimalPlaces?: number;
}

const Chart: React.FC<ChartProps> = ({ data, height, displayedDecimalPlaces = 1 }) => {
  const { isDark } = useThemeStore();

  const langMap: Record<string, string> = {
    'zh': 'zh-CN',
    'en': 'en-US',
    'tw': 'zh-TW',
    'zh-TW': 'zh-TW',
    'zh-CN': 'zh-CN',
    'en-US': 'en-US'
  };

  // If no data, provide two dummy points to force axes to render
  const hasData = data && data.length > 0;
  const chartData = hasData
    ? data
    : [
        { time: '', value: 0 },
        { time: '', value: 0 }
      ];

  // Determine if the overall PnL is positive or negative for line color
  const isPositive = useMemo(() => {
    if (chartData.length === 0) return false;
    const lastValue = chartData[chartData.length - 1].value;
    const firstValue = chartData[0].value;
    return lastValue >= firstValue;
  }, [chartData]);

  // Get colors based on theme and PnL direction
  const lineColor = isPositive 
    ? (isDark ? '#0BDE8D' : '#0BDE8D') // Green for positive
    : (isDark ? '#FF5169' : '#FF5169'); // Red for negative

  const textColor = isDark ? '#FFFFFF' : '#A1A3C4';
  const gridColor = isDark ? '#A1A3C4' : '#FFFFFF';

  // Calculate Y-axis width based on the maximum number of digits
  const yAxisWidth = useMemo(() => {
    if (chartData.length === 0) return 20;
    
    const maxValue = Math.max(...chartData.map(d => Math.abs(d.value)));
    const maxDigits = Math.ceil(Math.log10(maxValue + 1)); // +1 to handle 0
    
    // Calculate maximum decimal places
    const maxDecimalPlaces = Math.max(
      ...chartData.map(d => {
        const str = d.value.toString();
        const decimalIndex = str.indexOf('.');
        return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
      })
    );
    
    // Since we're using toFixed(1), we'll always show 1 decimal place
    // const displayedDecimalPlaces = 1; // Now this comes from props

    // Base width calculation: ~7px per digit
    let width = maxDigits * 7 + 16; // 16px base padding

    // Add space for decimal point and decimal digits
    if (displayedDecimalPlaces > 0) {
      width += 3; // Space for decimal point
      width += displayedDecimalPlaces * 7; // Space for decimal digits
    }
    
    // Check if any values are negative (need space for minus sign)
    const hasNegative = chartData.some(d => d.value < 0);
    if (hasNegative) {
      width += 6; // Extra space for minus sign
    }

    return Math.max(20, Math.min(width, 100)); // Min 20px, max 100px
  }, [chartData, displayedDecimalPlaces]);

  // 计算需要显示的时间标签索引
  const tickIndexes = useMemo(() => {
    if (chartData.length === 0) return [];
    
    const indexes = [];
    
    // 总是显示第一个点
    indexes.push(0);
    
    // 根据数据点数量动态调整中间点的间隔
    let interval;
    if (chartData.length <= 5) interval = 1;
    else if (chartData.length <= 10) interval = 2;
    else if (chartData.length <= 20) interval = 3;
    else if (chartData.length <= 30) interval = 5;
    else interval = Math.floor(chartData.length / 6);
    
    // 添加中间的点
    for (let i = interval; i < chartData.length - 1; i += interval) {
      indexes.push(i);
    }
    
    // 总是显示最后一个点（如果不是第一个点）
    if (chartData.length > 1) {
      const lastIndex = chartData.length - 1;
      if (!indexes.includes(lastIndex)) {
        indexes.push(lastIndex);
      }
    }
    
    return indexes;
  }, [chartData.length]);

  // 自定义时间格式化函数
  const formatTimeLabel = (value: string, index: number) => {
    // 如果时间是日期格式，可以进行格式化
  let rawLang = localStorage.getItem('preferred-language') || localStorage.getItem('preferred-language') || 'zh';
  let lang = langMap[rawLang] || rawLang || 'zh-CN';

    if (value.includes('-') || value.includes('/')) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString(lang, { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
    return value;
  };

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{
            top: 0,
            right: 20,
            left: 0,
            bottom: 0,
          }}
        >
          <XAxis 
            dataKey="time" 
            axisLine={{ stroke: '#A1A3C4', strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: 10, fill: textColor }}
            interval={0}
            height={20}
            tickFormatter={(value, index) => {
              // 只显示指定索引的标签
              if (tickIndexes.includes(index)) {
                return formatTimeLabel(value, index);
              }
              return '';
            }}
            allowDataOverflow={true}
          />
          <YAxis 
            axisLine={{ stroke: '#A1A3C4', strokeWidth: 1 }}
            tickLine={{ stroke: '#A1A3C4', strokeWidth: 1 }}
            tick={{ fontSize: 10, fill: textColor }}
            domain={hasData ? ['dataMin', 'dataMax'] : [0, 1]}
            tickFormatter={(value) => value.toFixed(displayedDecimalPlaces)}
            width={yAxisWidth}
            allowDataOverflow={true}
          />
          <ReferenceLine y={0} stroke={gridColor} strokeDasharray="2 2" />
          {/* <Tooltip
            formatter={(value: number) => [`${value.toFixed(displayedDecimalPlaces)}`]}
            labelStyle={{ color: textColor, fontSize: 12, fontFamily: 'font-inter' }}
            contentStyle={{ background: isDark ? '#1E203E' : '#1E203E', border: 'none', borderRadius: 4, color: textColor, fontSize: 12, fontFamily: 'font-inter' }}
            cursor={{ stroke: gridColor, strokeDasharray: '2 2' }}
          /> */}
          {hasData && (
            <Line
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ 
                r: 2, 
                fill: lineColor,
                strokeWidth: 0
              }}
              activeDot={{ 
                r: 3, 
                fill: lineColor,
                strokeWidth: 2,
                stroke: isDark ? '#1F2937' : '#FFFFFF'
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;
